import React, { useRef, useState } from "react";
import { Panel } from "@/pages/admin/shared";
import { Button } from "@/components/ui/button";
import { Download, Upload, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "@/pages/admin/api";

export default function BackupRestore() {
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const exportData = async () => {
    setBusy(true);
    try {
      const r = await api.get("/backup/export");
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `simple-invoice-backup-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Backup downloaded");
    } catch { toast.error("Export failed"); }
    finally { setBusy(false); }
  };

  const importData = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!window.confirm("This will REPLACE all content, settings, socials and FAQs. Continue?")) return;
    setBusy(true);
    try {
      const text = await f.text();
      const payload = JSON.parse(text);
      await api.post("/backup/restore", payload);
      toast.success("Backup restored");
    } catch { toast.error("Restore failed — invalid file"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <div className="space-y-6">
      <Panel title="Manual Backup" subtitle="Download all site content, settings, socials and FAQs as JSON">
        <Button data-testid="backup-export" onClick={exportData} disabled={busy} className="rounded-xl bg-blue-600 hover:bg-blue-700 text-white"><Download size={14} className="mr-1" /> Download Backup</Button>
      </Panel>

      <Panel title="Restore" subtitle="Upload a JSON backup to overwrite current data">
        <div className="rounded-xl bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 p-4 mb-4 flex items-start gap-3">
          <AlertTriangle size={16} className="text-amber-600 mt-0.5 shrink-0" />
          <div className="text-xs text-amber-800 dark:text-amber-300">Restore replaces existing content. This action cannot be undone — take a fresh backup first.</div>
        </div>
        <input ref={fileRef} type="file" accept="application/json" onChange={importData} className="hidden" data-testid="backup-file-input" />
        <Button data-testid="backup-import" onClick={() => fileRef.current?.click()} disabled={busy} variant="outline" className="rounded-xl"><Upload size={14} className="mr-1" /> Restore from file</Button>
      </Panel>

      <Panel title="Automatic Backup" subtitle="Coming soon — schedule daily/weekly automated snapshots">
        <div className="text-sm text-muted-foreground">Configure a cron job or connect a storage provider (S3, Drive) to enable automatic backups.</div>
      </Panel>
    </div>
  );
}
